import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { buscarOperacao, atualizarOperacao, listarFiliais } from '../../../services/montagemPalete';
import { listarClientes } from '../../../services/clientes';
import userService from '../../../services/users';
import { listarConferentes } from '../../../services/conferente';
import type { Filial, LocalPreCarregamento } from '../../../types/montagemPalete';
import '../MontagemPaletes.css';

interface ClienteOption {
  id: number;
  razao_social: string;
  cnpj: string;
}

interface UsuarioOption {
  id: number;
  nome: string;
  tipo_entidade?: 'usuario' | 'conferente';
}

const MontagemPaleteEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioOption[]>([]);

  const [formData, setFormData] = useState({
    cliente_id: '' as string | number,
    filial_id: '' as string | number,
    local_pre_carregamento: 'base_transportadora' as LocalPreCarregamento,
    observacoes: '',
    conferente_id: '' as string | number,
    conferente_tipo_original: '' as '' | 'usuario' | 'conferente'
  });

  useEffect(() => {
    listarClientes({ limit: 200, ativo: 'true' }).then(res => {
      if (res.success && res.clientes) {
        setClientes(res.clientes.map((c: any) => ({ id: c.id, razao_social: c.razao_social, cnpj: c.cnpj })));
      }
    }).catch((err: any) => setError(err?.message || 'Erro ao carregar clientes'));

    listarFiliais({ ativo: 'true' }).then(res => {
      if (res.success) setFiliais(res.filiais);
    }).catch((err: any) => setError(err?.message || 'Erro ao carregar filiais'));

    // Carregar conferentes de ambas entidades (usuarios e nova tabela conferentes)
    Promise.all([
      userService.getUsers({ limit: 200, ativo: true, tipo: 'conferente' }).catch(() => ({ usuarios: [] })),
      listarConferentes({ limit: 200, status: 'aprovado' }).catch(() => ({ success: false, conferentes: [] }))
    ]).then(([usersRes, confRes]) => {
      const fromUsuarios: UsuarioOption[] = (usersRes.usuarios || []).map((u: any) => ({
        id: u.id, nome: u.nome, tipo_entidade: 'usuario' as const
      }));
      const fromConferentes: UsuarioOption[] = ((confRes as any).conferentes || []).map((c: any) => ({
        id: c.id, nome: `${c.nome} (Conferente CPF)`, tipo_entidade: 'conferente' as const
      }));
      setUsuarios([...fromUsuarios, ...fromConferentes]);
    }).catch((err: any) => setError(err?.message || 'Erro ao carregar conferentes'));
  }, []);

  useEffect(() => {
    const carregar = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const result = await buscarOperacao(parseInt(id));
        if (result.success) {
          const op = result.operacao;
          // Montar value composto "tipo_id" para o select de conferente
          let conferenteValue = '';
          if (op.novo_conferente_id) {
            conferenteValue = `conferente_${op.novo_conferente_id}`;
          } else if (op.conferente_id) {
            conferenteValue = `usuario_${op.conferente_id}`;
          }
          const tipoOriginal = op.novo_conferente_id ? 'conferente' : (op.conferente_id ? 'usuario' : '');
          setFormData({
            cliente_id: op.cliente_id || '',
            filial_id: op.filial_id || '',
            local_pre_carregamento: op.local_pre_carregamento,
            observacoes: op.observacoes || '',
            conferente_id: conferenteValue,
            conferente_tipo_original: tipoOriginal as '' | 'usuario' | 'conferente'
          });
        }
      } catch (err) {
        setError('Erro ao carregar operação');
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setError('');
    setSaving(true);

    try {
      // Determinar se o conferente selecionado é da nova entidade ou do usuarios
      // O value do select tem formato "tipo_id" (ex: "usuario_4" ou "conferente_3")
      let conferenteTipo = '';
      let conferenteIdNum: number | null = null;
      if (formData.conferente_id && typeof formData.conferente_id === 'string' && formData.conferente_id.includes('_')) {
        const [tipo, idStr] = formData.conferente_id.split('_');
        conferenteTipo = tipo;
        conferenteIdNum = parseInt(idStr);
      }

      const payload: any = {
        cliente_id: formData.cliente_id ? Number(formData.cliente_id) : undefined,
        filial_id: formData.filial_id ? Number(formData.filial_id) : undefined,
        local_pre_carregamento: formData.local_pre_carregamento,
        observacoes: formData.observacoes || undefined
      };

      if (conferenteTipo === 'conferente' && conferenteIdNum) {
        payload.novo_conferente_id = conferenteIdNum;
        payload.conferente_id = null;
      } else if (conferenteTipo === 'usuario' && conferenteIdNum) {
        payload.conferente_id = conferenteIdNum;
        payload.novo_conferente_id = null;
      }
      const result = await atualizarOperacao(parseInt(id), payload);
      if (result.success) {
        navigate(`/montar-carga/${id}`);
      }
    } catch (err: any) {
      setError(err?.message || 'Erro ao atualizar operação');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <button className="montagem-back-link" onClick={() => navigate(`/montar-carga/${id}`)}>
        <i className="fas fa-arrow-left"></i> Voltar
      </button>

      <div className="card">
        <div className="card-header">
          <h2>
            <i className="fas fa-pallet" style={{ marginRight: 8 }}></i>
            Editar Operação #{id}
          </h2>
        </div>
        <div className="card-body">
          {error && <div className="montagem-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="montagem-form-grid">
              <div className="form-group">
                <label>Cliente</label>
                <select
                  className="form-control"
                  value={formData.cliente_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, cliente_id: e.target.value }))}
                >
                  <option value="">Selecione um cliente</option>
                  {clientes.map(c => (
                    <option key={c.id} value={c.id}>{c.razao_social} - {c.cnpj}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Filial *</label>
                <select
                  className="form-control"
                  value={formData.filial_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, filial_id: e.target.value }))}
                  required
                >
                  <option value="">Selecione uma filial</option>
                  {filiais.map(f => (
                    <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social} - {f.cnpj}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Local do Pré-Carregamento *</label>
                <select
                  className="form-control"
                  value={formData.local_pre_carregamento}
                  onChange={(e) => setFormData(prev => ({ ...prev, local_pre_carregamento: e.target.value as LocalPreCarregamento }))}
                  required
                >
                  <option value="base_transportadora">Base da Transportadora</option>
                  <option value="cliente">No Cliente</option>
                </select>
              </div>

              <div className="form-group">
                <label>Conferente</label>
                <select
                  className="form-control"
                  value={formData.conferente_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, conferente_id: e.target.value }))}
                >
                  <option value="">Selecione um conferente</option>
                  {usuarios.map(u => (
                    <option key={`${u.tipo_entidade}_${u.id}`} value={`${u.tipo_entidade}_${u.id}`}>{u.nome}</option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Observações</label>
                <textarea
                  className="form-control"
                  value={formData.observacoes}
                  onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                  rows={3}
                  placeholder="Observações gerais da operação..."
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button type="button" className="btn btn-outline" onClick={() => navigate(`/montar-carga/${id}`)}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <div className="loading-spinner" style={{ width: 16, height: 16 }}></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MontagemPaleteEdit;
